import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, UserCheck, UserX, Pencil, X } from 'lucide-react';
import { api } from '@/services/api';
import type { AdminUser } from '@/types';
import { lightImpact, notificationSuccess, notificationError } from '@/utils/haptics';
import toast from 'react-hot-toast';

const UserManager = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    pin: '',
    role: 'adult' as 'admin' | 'adult' | 'teen' | 'child',
    budgetLimit: 100,
    enabled: true,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await api.getUsers();
      setUsers(data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.updateUser(editingUser.id, {
          name: formData.name,
          role: formData.role,
          budgetLimit: formData.budgetLimit,
          enabled: formData.enabled,
        });
        toast.success('User updated');
        await notificationSuccess();
      } else {
        await api.createUser({
          name: formData.name,
          pin: formData.pin,
          role: formData.role,
          budgetLimit: formData.budgetLimit,
          budgetUsed: 0,
          enabled: true,
        });
        toast.success('User created');
        await notificationSuccess();
      }
      setShowForm(false);
      setEditingUser(null);
      resetForm();
      loadUsers();
    } catch (err) {
      toast.error('Failed to save user');
      await notificationError();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      pin: '',
      role: 'adult',
      budgetLimit: 100,
      enabled: true,
    });
  };

  const startEdit = (user: AdminUser) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      pin: '',
      role: user.role,
      budgetLimit: user.budgetLimit,
      enabled: user.enabled,
    });
    setShowForm(true);
  };

  const toggleUser = async (user: AdminUser) => {
    try {
      await api.updateUser(user.id, { enabled: !user.enabled });
      toast.success(user.enabled ? 'User disabled' : 'User enabled');
      loadUsers();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  const roleColors: Record<string, string> = {
    admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    adult: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    teen: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    child: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">Family Members</h2>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={async () => {
            await lightImpact();
            setEditingUser(null);
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl gradient-bg text-white text-sm font-semibold
                     shadow-glow min-h-12"
        >
          <Plus className="w-4 h-4" />
          Add User
        </motion.button>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  {editingUser ? 'Edit Member' : 'Add Family Member'}
                </h3>
                <motion.button whileTap={{ scale: 0.9 }} onClick={() => setShowForm(false)} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 min-h-12 min-w-12 flex items-center justify-center">
                  <X className="w-5 h-5 text-slate-500" />
                </motion.button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100
                               focus:outline-none focus:ring-2 focus:ring-primary-500/30 text-sm font-medium"
                    placeholder="e.g. Sarah"
                  />
                </div>

                {!editingUser && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">PIN (4 digits)</label>
                    <input
                      type="password"
                      value={formData.pin}
                      onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                      required={!editingUser}
                      maxLength={4}
                      pattern="[0-9]{4}"
                      className="w-full px-4 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100
                                 focus:outline-none focus:ring-2 focus:ring-primary-500/30 text-sm font-medium"
                      placeholder="1234"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Role</label>
                  <div className="grid grid-cols-4 gap-2">
                    {(['adult', 'teen', 'child', 'admin'] as const).map((role) => (
                      <motion.button
                        key={role}
                        type="button"
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setFormData({ ...formData, role })}
                        className={`py-2.5 rounded-xl text-xs font-bold capitalize transition-colors min-h-12
                          ${formData.role === role
                            ? 'gradient-bg text-white shadow-glow'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                          }`}
                      >
                        {role}
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">
                    Budget Limit: {formData.budgetLimit}%
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={100}
                    value={formData.budgetLimit}
                    onChange={(e) => setFormData({ ...formData, budgetLimit: Number(e.target.value) })}
                    className="w-full accent-primary-600 h-2"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <motion.button
                    type="submit"
                    whileTap={{ scale: 0.97 }}
                    className="flex-1 py-3.5 rounded-xl gradient-bg text-white text-sm font-bold shadow-glow min-h-12"
                  >
                    {editingUser ? 'Update' : 'Create'}
                  </motion.button>
                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setShowForm(false)}
                    className="flex-1 py-3.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-bold min-h-12"
                  >
                    Cancel
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* User List */}
      <div className="space-y-2">
        {users.map((user, index) => (
          <motion.div
            key={user.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="flex items-center justify-between p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0
                ${user.enabled ? 'gradient-bg text-white' : 'bg-slate-200 text-slate-400 dark:bg-slate-700 dark:text-slate-500'}`}>
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">{user.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${roleColors[user.role]}`}>
                    {user.role}
                  </span>
                  <div className="w-16 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary-500"
                      style={{ width: `${(user.budgetUsed / Math.max(user.budgetLimit, 1)) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <motion.button
                whileTap={{ scale: 0.85 }}
                onClick={() => startEdit(user)}
                className="p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors min-h-12 min-w-12 flex items-center justify-center"
                aria-label="Edit user"
              >
                <Pencil className="w-4 h-4 text-slate-500" />
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.85 }}
                onClick={() => toggleUser(user)}
                className="p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors min-h-12 min-w-12 flex items-center justify-center"
                aria-label={user.enabled ? 'Disable user' : 'Enable user'}
              >
                {user.enabled ? (
                  <UserCheck className="w-4 h-4 text-emerald-500" />
                ) : (
                  <UserX className="w-4 h-4 text-red-500" />
                )}
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default UserManager;
